<template>
  <div>
    <v-row
      v-if="manifest.already_registered"
    >
      <v-col>
        <h2 class="red--text text-center">本登録が完了しているためこの品目の編集・削除は行えません。</h2>
      </v-col>
    </v-row>

    <!-- 承認状況ラベル -->
    <v-row
      v-if="!['before_reserve', 'none_use_approval'].includes(manifest.approval_status)"
    >
      <v-col>
        <v-chip
          middle
          label
          :color="manifest.approval_status | approval_status_chip_color(manifest.manifest_registered)"
        >
          {{ manifest.approval_status | approval_status_chip_label(manifest.manifest_registered) }}
        </v-chip>
      </v-col>
    </v-row>
    <!-- /承認状況ラベル -->

    <!-- 判定日時・排出差戻理由 -->
    <v-row
      v-if="manifest.level === 'first_manifest' && manifest.manifest_type === 'dxe_form' && manifest.approval_status !== 'none_use_approval'"
    >
      <v-col cols="12" md="6">
        <div class="overline">
          排出判定日時
        </div>
        <h4>
          {{ manifest.approval_judgment_at | unix_datetime_format }}&nbsp;
        </h4>
        <v-divider />
      </v-col>
      <v-col cols="12" md="6">
        <div class="overline">
          排出差戻理由
        </div>
        <h4 class="red--text">
          {{ manifest.approval_remand_reason }}&nbsp;
        </h4>
        <v-divider />
      </v-col>
    </v-row>
    <!-- /判定日時・排出差戻理由 -->

    <!-- マニフェスト番号, 削除ボタン -->
    <v-row class="align-end">
      <v-col cols="12" md="4">
        <validation-provider
          v-if="manifest.manifest_type !== 'dxe_form' || includeNonSubscribeTrader || callableType === 'general'"
          v-slot="{ errors }"
          :vid="`manifest_no-${ tabIndex }`"
          name="マニフェスト番号"
          :rules="{
            numeric: (manifest.manifest_type !== 'dxe_form' || includeNonSubscribeTrader),
            length: (manifest.manifest_type !== 'dxe_form' || includeNonSubscribeTrader) ? 11 : false
          }"
        >
          <v-text-field
            v-model="manifest.manifest_no"
            :disabled="manifest.already_registered || loadByEmission"
            :error-messages="errors"
            dense
            hide-details="auto"
            label="マニフェスト番号"
            outlined
            persistent-placeholder
          >
            <template v-slot:label>
              <span>マニフェスト番号</span>
              <span class="ml-1">
                ({{ manifest.manifest_no?.length || 0 }} / 11)
              </span>
            </template>
            <template v-slot:counter></template>
          </v-text-field>
        </validation-provider>

      </v-col>
      <v-col cols="12" md="8" class="text-right">
        <v-btn
          :disabled="!canRemove"
          color="error"
          @click="removeItemButtonClickEvent"
        >
          品目の削除
        </v-btn>
      </v-col>
    </v-row>
    <!-- /マニフェスト番号, 削除ボタン -->


    <!-- 売上品目, 取扱品目, 廃棄物の名称 -->
    <v-row>
      <v-col
        v-if="isBilling && masterMode && callableType !== 'general'"
        cols="12"
        md="4"
      >
        <validation-provider
          v-slot="{ errors }"
          :vid="`sales_item_price_code-${ tabIndex }`"
          :class="masterMode && !loadByEmission ? 'requiredMark' : ''"
          :rules="{ required: masterMode && !loadByEmission }"
          name="売上品目"
        >
          <v-select
            v-model="manifest.sales_item_price_code"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            :items="salesItemPriceDetails"
            dense
            hide-details="auto"
            label="売上品目"
            outlined
            persistent-placeholder
            @change="salesItemPriceChangeEvent"
          >
          </v-select>
        </validation-provider>
      </v-col>
      <v-col cols="12" md="4">
        <validation-provider
          v-slot="{ errors }"
          :vid="`item_id-${ tabIndex }`"
          :class="{ 'requiredMark': !loadByEmission }"
          :name="callableType === 'general' ? '一般廃棄物品目' : '取扱品目'"
          :rules="{ required: !loadByEmission }"
        >
          <item-autocomplete
            v-model="manifest.item_id"
            :items="items"
            :error-messages="errors"
            :disabled="manifest.already_registered"
            :label="callableType === 'general' ? '一般廃棄物品目' : '取扱品目'"
            clearable
            dense
            hide-details="auto"
            outlined
            persistent-placeholder
            placeholder="品目名で検索する"
            @change="itemDropdownChangeEvent"
          />
        </validation-provider>
      </v-col>
      <v-col cols="12" md="4">
        <validation-provider
          v-slot="{ errors }"
          :vid="`waste_name-${ tabIndex }`"
          name="廃棄物の名称"
          rules="max:30"
        >
          <v-text-field
            v-model="manifest.waste_name"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            dense
            hide-details="auto"
            label="廃棄物の名称"
            outlined
            persistent-placeholder
          >
            <template v-slot:label>
              <span>廃棄物の名称</span>
              <span class="ml-1">
                ({{ manifest.waste_name?.length || 0 }} / 30)
              </span>
            </template>
            <template v-slot:counter></template>
          </v-text-field>
        </validation-provider>
      </v-col>
    </v-row>
    <!-- /売上品目, 取扱品目, 廃棄物の名称 -->

    <!-- 数量／数量単位 -->
    <v-row>
      <v-col cols="12" md="2">
        <validation-provider
          v-slot="{ errors }"
          :vid="`quantity-${ tabIndex }`"
          :class="{ 'requiredMark': !loadByEmission && ((dxeManifest && manifest.quantity_confirm_id === 2) || manifest.quantity_unit_id > 0) }"
          :custom-messages="customMessages"
          :rules="{
            required: !loadByEmission && ((dxeManifest && manifest.quantity_confirm_id === 2) || manifest.quantity_unit_id > 0),
            double: true,
            min_value: 0.001,
            max_value: 99999.999,
            quantityWithDecimal: { int_digits: 5, dec_digits: 3 }
          }"
          name="数量"
        >
          <v-text-field
            v-model="manifest.quantity"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            :show-size="1000"
            dense
            label="数量"
            hide-details="auto"
            outlined
            persistent-placeholder
            reverse
            type="number"
            @keydown.69.prevent
          />
        </validation-provider>
      </v-col>
      <v-col cols="12" md="4">
        <validation-provider
          v-slot="{ errors }"
          :vid="`quantity_unit_id-${ tabIndex }`"
          :class="{ 'requiredMark': !loadByEmission && ((dxeManifest && manifest.quantity_confirm_id === 2) || manifest.quantity > 0) }"
          :rules="{
            required: !loadByEmission && ((dxeManifest && manifest.quantity_confirm_id === 2) || manifest.quantity > 0)
          }"
          name="数量単位"
        >
          <v-select
            v-model="manifest.quantity_unit_id"
            :disabled="manifest.already_registered"
            :items="quantityUnits"
            :error-messages="errors"
            clearable
            dense
            hide-details="auto"
            label="数量単位       "
            outlined
            persistent-placeholder
          />
        </validation-provider>
      </v-col>
      <v-col cols="12" md="6">
        <validation-provider
          v-slot="{ errors }"
          :vid="`quantity_confirm_id-${ tabIndex }`"
          :class="{ 'requiredMark': !loadByEmission && manifest.manifest_type === 'dxe_form' && !includeNonSubscribeTrader }"

          :rules="{
            required: !loadByEmission && manifest.manifest_type === 'dxe_form' && !includeNonSubscribeTrader
          }"
          name="数量の確定者"
        >
          <v-select
            v-model="manifest.quantity_confirm_id"
            :clearable="!dxeManifest"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            :items="quantityConfirms"
            dense
            hide-details="auto"
            label="数量の確定者"
            outlined
            persistent-placeholder
          ></v-select>
        </validation-provider>
      </v-col>
    </v-row>
    <!-- /数量／数量単位 -->

    <!-- 荷姿／荷姿数量 -->
    <v-row>
      <v-col cols="12" md="4">
        <validation-provider
          v-slot="{ errors }"
          :vid="`packing_id-${ tabIndex }`"
          :class="{'requiredMark': !loadByEmission && manifest.manifest_type === 'dxe_form' && !includeNonSubscribeTrader }"
          :rules="{ required: !loadByEmission && manifest.manifest_type === 'dxe_form' && !includeNonSubscribeTrader }"
          name="荷姿"
        >
          <v-select
            v-model="manifest.packing_id"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            :items="packings"
            clearable
            dense
            hide-details="auto"
            label="荷姿"
            outlined
            persistent-placeholder
          />
        </validation-provider>
      </v-col>
      <v-col cols="12" md="2">
        <validation-provider
          v-slot="{ errors }"
          :vid="`packing_quantity-${ tabIndex }`"
          :rules="{ numeric: true, min_value: 0, max_value: 99999 }"
          name="荷姿の数量"
        >
          <v-text-field
            v-model="manifest.packing_quantity"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            :show-size="1000"
            dense
            label="荷姿の数量"
            hide-details="auto"
            outlined
            persistent-placeholder
            reverse
            required
            type="number"
            @keydown.69.prevent
          />
        </validation-provider>
      </v-col>
    </v-row>
    <!-- /荷姿／荷姿数量 -->

    <!-- 有害物質 -->
    <template v-if="!disableHazardousSubstance">
      <v-row>
        <v-col cols="12" md="6">
          <validation-provider
            v-slot="{ errors }"
            :vid="`hazardous_substance_1_id-${ tabIndex }`"
            :rules="{
            duplicateKeys: { keys: [
              manifest.hazardous_substance_2_id,
              manifest.hazardous_substance_3_id,
              manifest.hazardous_substance_4_id,
              manifest.hazardous_substance_5_id,
              manifest.hazardous_substance_6_id
            ] }
          }"
            name="有害物質1"
          >
            <v-select
              v-model="manifest.hazardous_substance_1_id"
              :disabled="disableHazardousSubstance"
              :error-messages="errors"
              :items="hazardousSubstances"
              clearable
              dense
              hide-details="auto"
              label="有害物質1"
              outlined
              persistent-placeholder
            />
          </validation-provider>
        </v-col>
        <v-col cols="12" md="6">
          <validation-provider
            v-slot="{ errors }"
            :vid="`hazardous_substance_2_id-${ tabIndex }`"
            :rules="{
            duplicateKeys: { keys: [
              manifest.hazardous_substance_1_id,
              manifest.hazardous_substance_3_id,
              manifest.hazardous_substance_4_id,
              manifest.hazardous_substance_5_id,
              manifest.hazardous_substance_6_id
            ] }
          }"
            name="有害物質2"
          >
            <v-select
              v-model="manifest.hazardous_substance_2_id"
              :disabled="disableHazardousSubstance"
              :error-messages="errors"
              :items="hazardousSubstances"
              clearable
              dense
              hide-details="auto"
              label="有害物質2"
              outlined
              persistent-placeholder
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            :vid="`hazardous_substance_3_id-${ tabIndex }`"
            :rules="{
            duplicateKeys: { keys: [
              manifest.hazardous_substance_1_id,
              manifest.hazardous_substance_2_id,
              manifest.hazardous_substance_4_id,
              manifest.hazardous_substance_5_id,
              manifest.hazardous_substance_6_id
            ] }
          }"
            name="有害物質3"
          >
            <v-select
              v-model="manifest.hazardous_substance_3_id"
              :disabled="disableHazardousSubstance"
              :error-messages="errors"
              :items="hazardousSubstances"
              clearable
              dense
              hide-details="auto"
              label="有害物質3"
              outlined
              persistent-placeholder
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            :vid="`hazardous_substance_4_id-${ tabIndex }`"
            :rules="{
            duplicateKeys: { keys: [
              manifest.hazardous_substance_1_id,
              manifest.hazardous_substance_2_id,
              manifest.hazardous_substance_3_id,
              manifest.hazardous_substance_5_id,
              manifest.hazardous_substance_6_id
            ] }
          }"
            name="有害物質4"
          >
            <v-select
              v-model="manifest.hazardous_substance_4_id"
              :disabled="disableHazardousSubstance"
              :error-messages="errors"
              :items="hazardousSubstances"
              clearable
              dense
              hide-details="auto"
              label="有害物質4"
              outlined
              persistent-placeholder
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            :vid="`hazardous_substance_5_id-${ tabIndex }`"
            :rules="{
            duplicateKeys: { keys: [
              manifest.hazardous_substance_1_id,
              manifest.hazardous_substance_2_id,
              manifest.hazardous_substance_3_id,
              manifest.hazardous_substance_4_id,
              manifest.hazardous_substance_6_id
            ] }
          }"
            name="有害物質5"
          >
            <v-select
              v-model="manifest.hazardous_substance_5_id"
              :disabled="disableHazardousSubstance"
              :error-messages="errors"
              :items="hazardousSubstances"
              clearable
              dense
              hide-details="auto"
              label="有害物質5"
              outlined
              persistent-placeholder
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <validation-provider
            v-slot="{ errors }"
            :vid="`hazardous_substance_6_id-${ tabIndex }`"
            :rules="{
            duplicateKeys: { keys: [
              manifest.hazardous_substance_1_id,
              manifest.hazardous_substance_2_id,
              manifest.hazardous_substance_3_id,
              manifest.hazardous_substance_4_id,
              manifest.hazardous_substance_5_id
            ] }
          }"
            name="有害物質6"
          >
            <v-select
              v-model="manifest.hazardous_substance_6_id"
              :disabled="disableHazardousSubstance"
              :error-messages="errors"
              :items="hazardousSubstances"
              clearable
              dense
              hide-details="auto"
              label="有害物質6"
              outlined
              persistent-placeholder
            />
          </validation-provider>
        </v-col>
      </v-row>
    </template>
    <!-- /有害物質 -->

    <!-- ルート一覧 -->
    <v-row>
      <v-col>
        <validation-provider
          v-slot="{ errors }"
          :vid="`transport_route_id-${ tabIndex }`"
          :class="{ 'requiredMark': !loadByEmission }"
          name="運搬ルート"
          :rules="{ required: !loadByEmission }"
        >
          <v-input
            v-model="manifest.transport_route_id"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            dense
            label="運搬ルート"
            outlined
            readonly
            hide-details="auto"
          />
        </validation-provider>
        <v-row>
          <v-col cols="12" md="12">
            <div class="my-4">
              <v-text-field
                v-model="searchByKeyword"
                :disabled="manifest.already_registered"
                append-icon="mdi-magnify"
                clearable
                dense
                hide-details="auto"
                label="絞り込み検索"
                outlined
                placeholder="ルート名・運搬先名・処分先名から検索"
                persistent-placeholder
                style="font-size: 90%"
              />
            </div>
          </v-col>
        </v-row>
        <div v-if="!manifest.already_registered && !manifest.can_change_transport_route" class="red--text"><strong>※&nbsp;予約登録済、発注で利用されているためルートの編集はできません。</strong></div>
        <div class="red--text" v-if="manifest.displayTransportAlert"><strong>※&nbsp;ルートを変更するとドライバーアプリの回収済以降の運搬量はクリアされます。</strong></div>
        <v-data-table
          :headers="transportRoutesTableHeaders"
          :items="!manifest.already_registered && !manifest.can_change_transport_route ? allTransportRoutes : transportRoutes"
          :item-class="transportRouteRowBackground"
          :items-per-page="-1"
          :search="searchByKeyword"
          class="elevation-1"
          disable-sort
          fixed-header
          hide-default-footer
          height="25vh"
          single-select
          @click:row="transportRowClick"
        >
          <template v-slot:[`item.name`]="{ item }">
            <div
              :title="item.name"
              class="line-clamp one-clamp"
            >
              {{ item.name }}
            </div>
          </template>
          <template v-slot:[`item.transporter_name`]="{ item }">
            <div
              :title="item.transporter_name"
              class="line-clamp one-clamp"
            >
              {{ item.transporter_name }}
            </div>
          </template>
          <template v-slot:[`item.trader_name`]="{ item }">
            <div
              :title="item.trader_name"
              class="line-clamp one-clamp"
            >
              {{ item.trader_name }}
            </div>
          </template>
          <template v-slot:[`item.disposer_location_name`]="{ item }">
            <div
              :title="item.disposer_location_name"
              class="line-clamp one-clamp"
            >
              {{ item.disposer_location_name }}
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <!-- /ルート一覧 -->

    <!-- ルート詳細 -->
    <transport-route-result
      :custom-mode="true"
      :emission="selectedEmission"
      :freeTypeEmission="freeTypeEmission"
      :form="manifest"
      :transport-route-id="manifest.transport_route_id"
      :master-mode="masterMode"
    />
    <!-- /ルート詳細 -->

    <!-- 処分方法 -->
     <v-card-title
      class="pt-0"
      >
      <span style="color: #0074BA">処分方法</span>
    </v-card-title>
    <v-row>
      <v-col cols="12" md="6">
        <validation-provider
          v-slot="{ errors }"
          :vid="`disposal_method_id-${ tabIndex }`"
          name="処分方法"
        >
          <v-select
            v-model="manifest.disposal_method_id"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            :items="disposalMethods"
            clearable
            dense
            hide-details="auto"
            label="処分方法"
            outlined
            persistent-placeholder
          ></v-select>
        </validation-provider>
      </v-col>
    </v-row>
    <!-- /処分方法 -->

    <!-- 連絡番号 -->
    <v-row>
      <v-col cols="12" md="6" class="pa-0">
        <v-row>
          <v-col cols="12">
            <validation-provider
              v-slot="{ errors }"
              :vid="`master_contact_number_1`"
              rules="contact_num_only|max:20"
              name="連絡番号１"
            >
              <v-text-field
                v-model="manifest.contact_number_1"
                :disabled="manifest.already_registered"
                :error-messages="errors"
                dense
                label="連絡番号１"
                hide-details="auto"
                outlined
                persistent-placeholder
              >
                <template v-slot:label>
                  <span>連絡番号１</span>
                  <span class="ml-1">
                ({{ manifest.contact_number_1?.length || 0 }} / 20)
              </span>
                </template>
                <template v-slot:counter></template>
                <template v-slot:append-outer>
                  <v-btn
                    color="primary"
                    :disabled="manifest.already_registered"
                    fab
                    x-small
                    elevation="0"
                    @click="addContactNumberClick"
                  >
                    <v-icon dark> mdi-plus </v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-expand-transition>
          <v-row v-if="displayContactNumber2">
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :vid="`master_contact_number_2`"
                rules="contact_num_only|max:20"
                name="連絡番号２"
              >
                <v-text-field
                  v-model="manifest.contact_number_2"
                  :disabled="manifest.already_registered"
                  :error-messages="errors"
                  dense
                  label="連絡番号２"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>連絡番号２</span>
                    <span class="ml-1">
                  ({{ manifest.contact_number_2?.length || 0 }} / 20)
                </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <v-btn
                      color="primary"
                      :disabled="manifest.already_registered"
                      fab
                      x-small
                      elevation="0"
                      @click="addContactNumberClick"
                    >
                      <v-icon dark>
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-expand-transition>
          <v-row v-if="displayContactNumber3">
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :vid="`master_contact_number_3`"
                rules="contact_num_only|max:20"
                name="連絡番号３"
              >
                <v-text-field
                  v-model="manifest.contact_number_3"
                  :disabled="manifest.already_registered"
                  :error-messages="errors"
                  dense
                  label="連絡番号３"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>連絡番号３</span>
                    <span class="ml-1">
                  ({{ manifest.contact_number_3?.length || 0 }} / 20)
                </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <v-btn
                      color="primary"
                      fab
                      x-small
                      elevation="0"
                      style="visibility: hidden"
                      @click="addContactNumberClick"
                    >
                      <v-icon dark> mdi-plus </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-expand-transition>
      </v-col>
      <v-col cols="12" md="6" class="pa-0">
        <v-row>
          <v-col cols="12">
            <validation-provider
              v-slot="{ errors }"
              :vid="`note_1-${ tabIndex }`"
              name="備考１"
              rules="max:25"
            >
              <v-text-field
                v-model="manifest.note_1"
                :disabled="manifest.already_registered"
                :error-messages="errors"
                dense
                hide-details="auto"
                label="備考１"
                outlined
                persistent-placeholder
              >
                <template v-slot:label>
                  <span>備考１</span>
                  <span class="ml-1">
                ({{ manifest.note_1?.length || 0 }} / 25)
              </span>
                </template>
                <template v-slot:counter></template>
                <template v-slot:append-outer>
                  <v-btn
                    color="primary"
                    :disabled="manifest.already_registered"
                    fab
                    x-small
                    elevation="0"
                    @click="addNoteClick"
                  >
                    <v-icon dark>
                      mdi-plus
                    </v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-expand-transition>
          <v-row v-if="displayNote2">
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :vid="`note_2-${ tabIndex }`"
                name="備考２"
                rules="max:25"
              >
                <v-text-field
                  v-model="manifest.note_2"
                  :disabled="manifest.already_registered"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="備考２"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>備考２</span>
                    <span class="ml-1">
                  ({{ manifest.note_2?.length || 0 }} / 25)
                </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <v-btn
                      color="primary"
                      :disabled="manifest.already_registered"
                      fab
                      x-small
                      elevation="0"
                      @click="addNoteClick"
                    >
                      <v-icon dark>
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-expand-transition>
          <v-row v-if="displayNote3">
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :vid="`note_3-${ tabIndex }`"
                name="備考３"
                rules="max:25"
              >
                <v-text-field
                  v-model="manifest.note_3"
                  :disabled="manifest.already_registered"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="備考３"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>備考３</span>
                    <span class="ml-1">
                  ({{ manifest.note_3?.length || 0 }} / 25)
                </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <v-btn
                      color="primary"
                      :disabled="manifest.already_registered"
                      fab
                      x-small
                      elevation="0"
                      @click="addNoteClick"
                    >
                      <v-icon dark>
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-expand-transition>
          <v-row v-if="displayNote4">
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :vid="`note_4-${ tabIndex }`"
                name="備考４"
                rules="max:25"
              >
                <v-text-field
                  v-model="manifest.note_4"
                  :disabled="manifest.already_registered"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="備考４"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>備考４</span>
                    <span class="ml-1">
                  ({{ manifest.note_4?.length || 0 }} / 25)
                </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <v-btn
                      color="primary"
                      :disabled="manifest.already_registered"
                      fab
                      x-small
                      elevation="0"
                      @click="addNoteClick"
                    >
                      <v-icon dark>
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-expand-transition>
        <v-expand-transition>
          <v-row v-if="displayNote5">
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :vid="`note_5-${ tabIndex }`"
                name="備考５"
                rules="max:25"
              >
                <v-text-field
                  v-model="manifest.note_5"
                  :disabled="manifest.already_registered"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="備考５"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>備考５</span>
                    <span class="ml-1">
                  ({{ manifest.note_5?.length || 0 }} / 25)
                </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <v-btn
                      color="primary"
                      fab
                      x-small
                      elevation="0"
                      style="visibility: hidden"
                    >
                      <v-icon dark> mdi-plus </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-expand-transition>
      </v-col>
    </v-row>
    <!-- /連絡番号 -->

    <!-- マニフェスト検索キーワード -->
    <v-row>
      <v-col cols="12" md="12">
        <validation-provider
          v-slot="{ errors }"
          name="マニフェスト検索キーワード"
          rules="max:25"
        >
          <v-text-field
            v-model="manifest.manifest_search_keywords.keyword"
            :error-messages="errors"
            dense
            hide-details="auto"
            label="マニフェスト検索キーワード"
            outlined
            persistent-placeholder
          >
            <template v-slot:label>
              <span>マニフェスト検索キーワード</span>
              <span class="ml-1">
                ({{ manifest.manifest_search_keywords.keyword?.length || 0 }} / 25)
              </span>
              <span
                v-if="callableType !== 'general'"
                class="red--text font-weight-bold"
              >
                &emsp;※&nbsp;DXEStation排出モードにも表示されます。&nbsp;
              </span>
            </template>
            <template v-slot:counter></template>
          </v-text-field>
        </validation-provider>
      </v-col>
    </v-row>
    <!-- /マニフェスト検索キーワード -->

    <!-- 再資源化等処理情報 -->
    <v-card-title
      v-if="callableType !== 'general' && (this.mode === 'transport_disposal' || this.mode === 'disposal')"
      class="pt-0"
      >
      <span style="color: #0074BA">再資源化等処理情報</span>
    </v-card-title>
    <v-row
      v-if="callableType !== 'general' && (this.mode === 'transport_disposal' || this.mode === 'disposal')"
    >
      <v-col cols="12" md="4">
        <validation-provider
          v-slot="{ errors }"
          :vid="`standard_quantity_type_id-${ tabIndex }`"
          name="基準重量"
        >
          <v-select
            v-model="manifest.standard_quantity_type"
            :disabled="manifest.already_registered"
            :error-messages="errors"
            :items="standardQuantityTypes"
            clearable
            dense
            hide-details="auto"
            label="基準重量"
            outlined
            persistent-placeholder
          ></v-select>
        </validation-provider>
      </v-col>
      <v-col cols="12" md="4">
        <validation-provider
            v-slot="{ errors }"
            :vid="`recycle_disposer_disposal_method_id-${ tabIndex }`"
            name="処分場処理方法"
        >
          <div class="field-with-copy">
            <div class="field-with-copy__control">
              <v-select
                  v-model="manifest.recycle_disposer_disposal_method_id"
                  :disabled="manifest.already_registered"
                  :error-messages="errors"
                  :items="recycleDisposerDisposalMethods"
                  clearable
                  dense
                  hide-details="auto"
                  label="処分場処理方法"
                  outlined
                  persistent-placeholder
              >
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-on="on" v-bind="attrs">
                    <v-list-item-content>
                      <template>
                        <div class="d-flex align-center flex-wrap py-1" style="gap: 8px;">
                          <v-chip color="primary" label text-color="white" x-small>処分場処理方法</v-chip>
                          <span class="font-weight-medium">{{ item.pattern_name || item.text }}</span>
                        </div>
                      </template>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  {{ item.pattern_name || item.text }}
                </template>
              </v-select>
            </div>
            <div class="select-actions">
              <choose-recycle-disposal-pattern-botton
                :disabled="!canPressDisposalPatternButton"
                :fixed-condition="recyclePatternDialogCondition"
                small-button
                button-caption="選択"
                only-method
                @choseRecycleDisposalPatternCallback="choseRecycleDisposalPatternCallback"
              />
            </div>
          </div>
        </validation-provider>
      </v-col>
    </v-row>

    <v-row
      v-if="callableType !== 'general' && displayDisposalMethodDetail && (this.mode === 'transport_disposal' || this.mode === 'disposal')"
    >
      <v-col cols="12" md="10">
        <recycle-step-table
          :headers="headers"
          :result-list="recycleDisposerDisposalMethodDetailsList"
          :reference-only="true"
          :hide-origin-ratio="true"
        />
      </v-col>
    </v-row>
    <!-- /再資源化等処理情報 -->

  </div>
</template>

<script>

import {mapActions, mapState} from 'vuex'
import TransportRouteResult from "../../../components/TransportRouteResult";
import ItemAutocomplete from "@/driver/components/ItemAutocomplete.vue";
import ChooseRecycleDisposalPatternBotton from '@/driver/components/ChooseRecycleDisposalPatternBotton.vue'

const STANDARD_QUANTITY_TYPES = [
  { value: 'fixed_quantity', text: '確定数量' },
  { value: 'waste_quantity', text: '廃棄物の数量' },
  { value: 'transport_quantity', text: '運搬業者の数量' },
  { value: 'disposal_quantity', text: '処分数量' },
]

/**
 *  @typedef {Object} Props
 *  @property {Object} manifest
 */
export default {
  name: 'SalesItemBlockForm',
  components: {
    TransportRouteResult,
    ItemAutocomplete,
    ChooseRecycleDisposalPatternBotton,
  },
  mixins: [],
  props: {
    manifest: {
      type: Object,
      default: () => null,
    },
    tabIndex: {
      type: Number,
      default: () => 0,
    },
    canRemove: {
      type: Boolean,
      default: () => false,
    },
    /**
     * 動作モード
     * collection: 受注登録
     * self_emission: 排出自己運搬
     * proxy_request: 他社運搬
     * general: 一般廃棄物
     */
    callableType: {
      type: String,
      default: null,
    },
    items: {
      type: Array,
      default: () => []
    },
    salesItemPriceDetails: {
      type: Array,
      default: () => []
    },
    quantityUnits: {
      type: Array,
      default: () => [],
    },
    quantityConfirms: {
      type: Array,
      default: () => [],
    },
    packings: {
      type: Array,
      default: () => [],
    },
    hazardousSubstances: {
      type: Array,
      default: () => [],
    },
    /** 運搬方法 */
    transportations: {
      type: Array,
      default: () => [],
    },
    disposalMethods: {
      type: Array,
      default: () => [],
    },
    masterMode: {
      type: Boolean,
      default: () => true
    },
    dxeManifest: {
      type: Boolean,
      default: () => false
    },
    selectedEmission: {
      type: Object,
      default: () => null
    },
    freeTypeEmission: {
      type: Object,
      default: () => null
    },
    loadByEmission: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      disableField: this.manifest.already_registered,
      displayDisposalMethodDetail: false,
      displayContactNumber2: false,
      displayContactNumber3: false,
      displayNote2: false,
      displayNote3: false,
      displayNote4: false,
      displayNote5: false,
      transportRoutesTableHeaders: [
        { value: "name", text: "ルート名称", width: "25%" },
        { value: "transporter_name", text: "区間１収集運搬業者", width: "25%" },
        { value: "trader_name", text: "区間１運搬先会社名", width: "25%" },
        { value: "disposer_location_name", text: "処分業者事業場名", width: "25%" },
      ],
      searchByKeyword: null,
      transportSelected: [],
      selectedRouteRow: null,
      includeNonSubscribeTrader: null,
      customMessages: {
        double: '数量は半角数字のみ使用できます'
      },
      headers: [
        { value: "before_disposal_ratio", text: "処分割合", width: "12%" },
        { value: "disposal_method", text: "処分方法", width: "26%" },
        { value: "after_disposal_ratio", text: "処理後割合", width: "12%" },
        { value: "recycle_disposed_item", text: "処理後物の大分類\r\n処理後物の種類", width: "30%" },
        { value: "final_disposal", text: "最終処分", width: "15%" },
        { value: "actions", text: "", width: "5%", align: "end" },
      ],
      recycleDisposerDisposalMethods: [],
      selectedRecycleDisposerDisposalMethodForm: null,
      recycleDisposerDisposalMethodDetailsList: [],
      transportRoutes: [],
      allTransportRoutes: [],
      selectedRouteDisposerId: null,
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      isBilling: 'use_billing',
      mode: 'mode',
    }),
    disableHazardousSubstance () {
      let item = this.items.find( (v) => v.value === this.manifest.item_id)
      if (item === undefined) {
        this.removeHazardousSubstances()
        return true
      }

      if (this.manifest.already_registered) {
        return true
      }

      if (!item.use_harmful) {
        this.removeHazardousSubstances()
      }
      return !item.use_harmful
    },
    standardQuantityTypes () {
      return STANDARD_QUANTITY_TYPES
    },
    canPressDisposalPatternButton () {
      return (!!this.manifest.item_id && !!this.manifest.transport_route_id)
    },
    recyclePatternDialogCondition () {
      const selectedRoute = this.getSelectedTransportRoute()
      if (!selectedRoute) {
        return {
          item_id: this.manifest.item_id,
          disposer_id: null,
          disposer_location_id: null,
          disposer_location_code: null,
        }
      }

      return {
        item_id: this.manifest.item_id,
        disposer_id: selectedRoute.disposer_id,
        disposer_location_id: selectedRoute.disposer_location_id,
        disposer_location_code: selectedRoute.disposer_location_code,
      }
    },
  },
  watch: {

    /**
     * JWNET取扱品目 または、運搬ルートのどちらかが未選択であれば
     * 選択中の処分場処理方法ID, 処分場処理方法リストをクリアする
     */
    manifest: {
      handler() {
        if (!this.canPressDisposalPatternButton) {
          this.manifest.recycle_disposer_disposal_method_id = null;
          this.recycleDisposerDisposalMethodDetailsList = [];
        }
      },
      deep: true,
    },

    /**
     * 動作モードの監視
     * @param newValue { String }
     * @param oldValue { String }
     */
    callableType: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.refreshTransportRoutes();
          this.$emit('changeManifestTypeCallback', this.manifest);
        }
      },
      deep: true,
      immediate: true
    },
    'manifest.recycle_disposer_disposal_method_id': {
      handler (newValue) {
        this.loadRecycleDisposerDisposalMethodDetails(newValue)
      },
      immediate: true,
    },
  },
  created() {
    this.controlNotes()
    this.controlContactNumbers()
    // 初期表示時にルート一覧を取得し、選択中ルートの整合をチェックする
    this.refreshTransportRoutes().then(() => {
      this.afterLoadRouteValidation();
    });
  },
  methods: {
    ...mapActions('DriverMasterSearchTransportRoute', {
      fetchTransportRoutes: 'search',
    }),
    /**
     * 処分場処理方法検索
     */
    ...mapActions('DriverMasterSearchRecycleDisposerDisposalMethod', {
      fetchRecycleDisposerDisposalMethods: 'index',
    }),
    /**
     * 処分場処理方法
     */
    ...mapActions("DriverMasterRecycleDisposerDisposalMethod", {
      fetchEdit: "edit",
    }),
    ...mapActions('snackbar', {
      showError: 'showError'}),

    /**
     * 処分場処理方法検索モーダルの選択結果を反映する
     * @param payload {{ pattern: { id: number, type: string, pattern_name: string } }}
     */
    choseRecycleDisposalPatternCallback (payload) {
      const pattern = payload?.pattern
      if (!pattern || pattern.type !== 'methods') {
        return
      }

      const selectedId = pattern.id
      if (!this.recycleDisposerDisposalMethods.some(method => method.value === selectedId)) {
        this.recycleDisposerDisposalMethods.push({
          value: selectedId,
          text: pattern.pattern_name,
        })
      }
      this.manifest.recycle_disposer_disposal_method_id = selectedId
    },
    /**
     * 品目削除イベントを親へ通知する.
     */
    removeItemButtonClickEvent () {
      this.$emit("removeItem")
    },

    /**
     * 備考欄の表示状態を初期データに合わせて復元する.
     */
    controlNotes () {
      if (this.manifest.note_5) {
        this.displayNote2 = true
        this.displayNote3 = true
        this.displayNote4 = true
        this.displayNote5 = true
      } else if (this.manifest.note_4) {
        this.displayNote2 = true
        this.displayNote3 = true
        this.displayNote4 = true
      } else if (this.manifest.note_3) {
        this.displayNote2 = true
        this.displayNote3 = true
      } else if (this.manifest.note_2) {
        this.displayNote2 = true
      }
    },

    /**
     * 連絡番号欄の表示状態を初期データに合わせて復元する.
     */
    controlContactNumbers () {
      if (this.manifest.contact_number_3) {
        this.displayContactNumber2 = true
        this.displayContactNumber3 = true
      } else if (this.manifest.contact_number_2) {
        this.displayContactNumber2 = true
      }
    },

    /**
     * 選択中の運搬ルートの背景を強調表示する.
     * @param item
     * @return {string}
     */
    transportRouteRowBackground ( item ) {
      return (item.id === this.manifest.transport_route_id) ? 'v-data-table__selected' : ''
    },

    /**
     * 運搬ルート選択時に、関連する品目や処分情報を同期する.
     * @param item
     * @param row
     */
    transportRowClick (item, row) {
      // 予約登録済の場合は変更不可
      if (!this.manifest.can_change_transport_route) {
        return false
      }
      this.manifest.transport_route_id = item.id
      this.manifest.disposal_method_id = item.disposal_method_id
      row.select(true)
      this.selectedRouteRow = row
      this.includeNonSubscribeTrader = item.include_non_subscribe_trader
      this.manifest.includeNonSubscribeTrader = item.include_non_subscribe_trader
      this.manifest.include_non_subscribe_trader = item.include_non_subscribe_trader

      this.$emit('changeManifestTypeCallback', this.manifest);
      this.refreshRecycleDisposerDisposalMethods(item)
    },

    /**
     * 有害物質の入力を全てクリアする.
     */
    removeHazardousSubstances () {
      this.manifest.hazardous_substance_1_id = null
      this.manifest.hazardous_substance_2_id = null
      this.manifest.hazardous_substance_3_id = null
      this.manifest.hazardous_substance_4_id = null
      this.manifest.hazardous_substance_5_id = null
      this.manifest.hazardous_substance_6_id = null
    },

    /**
     * 連絡番号の入力欄を1段階増やす.
     */
    addContactNumberClick () {
      if (this.displayContactNumber2) {
         this.displayContactNumber3 = true
      } else {
         this.displayContactNumber2 = true
      }
    },

    /**
     * 備考の入力欄を1段階増やす.
     */
    addNoteClick () {
      if (this.displayNote4) {
         this.displayNote5 = true
      } else if (this.displayNote3) {
         this.displayNote4 = true
      } else if (this.displayNote2) {
         this.displayNote3 = true
      } else {
        this.displayNote2 = true
      }
    },

    /**
     * 現在選択中の運搬ルートを一覧から取得する.
     * @return {Object|null}
     */
    getSelectedTransportRoute () {
      return this.transportRoutes.find((route) => route.id === this.manifest.transport_route_id)
        || this.allTransportRoutes.find((route) => route.id === this.manifest.transport_route_id)
        || null
    },

    /**
     * 処分場処理方法詳細の表示一覧を生成する.
     * @return {Array}
     */
    buildRecycleDisposerDisposalMethodDetails () {
      const details = this.selectedRecycleDisposerDisposalMethodForm?.recycle_disposer_disposal_method_details
        || []

      return details
        .map((detail) => {
          return {
            before_disposal_ratio: detail.before_disposal_ratio,
            recycle_disposal_method_name: detail.recycle_disposal_method_name,
            after_disposal_method_names: detail.after_disposal_method_names,
            after_disposal_ratio: detail.after_disposal_ratio,
            recycle_disposed_item_parent_name: detail.recycle_disposed_item_parent_name,
            recycle_disposed_item_name: detail.recycle_disposed_item_name,
            after_type_label: detail.after_type_label,
            final_disposal: detail.final_disposal,
          }
        })
    },

    /**
     * 選択された処分場処理方法の詳細を個別取得する.
     * @param recycleDisposerDisposalMethodId
     * @return {Promise<void>}
     */
    loadRecycleDisposerDisposalMethodDetails (recycleDisposerDisposalMethodId) {
      if (!recycleDisposerDisposalMethodId) {
        this.selectedRecycleDisposerDisposalMethodForm = null
        this.recycleDisposerDisposalMethodDetailsList = []
        this.displayDisposalMethodDetail = false
        return Promise.resolve()
      }

      return this.fetchEdit({ id: recycleDisposerDisposalMethodId }).then((content) => {
        this.displayDisposalMethodDetail = true;
        this.selectedRecycleDisposerDisposalMethodForm = content
        this.recycleDisposerDisposalMethodDetailsList = this.buildRecycleDisposerDisposalMethodDetails()
      }).catch(() => {
        this.selectedRecycleDisposerDisposalMethodForm = null
        this.recycleDisposerDisposalMethodDetailsList = []
        this.displayDisposalMethodDetail = false
      })
    },

    /**
     * 品目と処分先に応じて処分場処理方法の候補を再取得する.
     * @param transportRoute
     * @return {Promise<void>}
     */
    refreshRecycleDisposerDisposalMethods (transportRoute = null) {
      const selectedRoute = transportRoute || this.getSelectedTransportRoute()

      if (!this.manifest.item_id || !selectedRoute || !selectedRoute.id) {
        this.recycleDisposerDisposalMethods = []
        this.selectedRecycleDisposerDisposalMethodForm = null
        this.recycleDisposerDisposalMethodDetailsList = []
        this.displayDisposalMethodDetail = false
        this.manifest.recycle_disposer_disposal_method_id = null
        return Promise.resolve()
      }

      return this.fetchRecycleDisposerDisposalMethods({
        item_id: this.manifest.item_id,
        disposer_id: selectedRoute.disposer_id,
        disposer_location_code: selectedRoute.disposer_location_code,
      }).then((content) => {
        this.recycleDisposerDisposalMethods = content.recycle_disposer_disposal_methods || []
        if (selectedRoute.disposer_id !== this.selectedRouteDisposerId && this.selectedRouteDisposerId !== null) {
          this.manifest.recycle_disposer_disposal_method_id = null
          this.displayDisposalMethodDetail = false
        }
        // 編集画面を開いた時、選択中の処分場処理方法が非表示化されている場合があるので、判定して未選択状態にする
        const isRecycleDisposerDisposalMethod = this.recycleDisposerDisposalMethods.find(
          v => v.id === this.manifest.recycle_disposer_disposal_method_id
        );
        if (!isRecycleDisposerDisposalMethod) {
          this.manifest.recycle_disposer_disposal_method_id = null
          this.displayDisposalMethodDetail = false
        }

        this.selectedRouteDisposerId = selectedRoute.disposer_id
      }).catch(() => {
        this.recycleDisposerDisposalMethods = []
        this.selectedRecycleDisposerDisposalMethodForm = null
        this.recycleDisposerDisposalMethodDetailsList = []
        this.displayDisposalMethodDetail = false
      })
    },

    /**
     * 運搬ルートの洗替
     * @param func
     * @return {Promise<void>}
     */
    refreshTransportRoutes (func = null) {

      const isCollection = `${this.callableType}` === 'collection'
      const isSelfEmission = `${this.callableType}` === 'self_emission'
      const isProxyRequest = `${this.callableType}` === 'proxy_request'
      const isGeneral = `${this.callableType}` === 'general'

      const visibleRoutesPromise = this.fetchTransportRoutes({
        without_invisible: true,
        first_trader_owned: isCollection,
        self_emission: isSelfEmission,
        self_emission_include_own: isSelfEmission,
        proxy_request: isProxyRequest,
        general: isGeneral,
        item_id: this.manifest.item_id,
        item_type: this.callableType === 'general' ? 'GeneralItem' : 'Item',
        sales_item_price_code: this.manifest.sales_item_price_code
      }).then((content) => {
        this.transportRoutes = content.transport_routes

        // 運搬ルートが1件のみの場合は自動選択する
        if (content.transport_routes.length === 1) {
          const route = content.transport_routes[0]
          this.manifest.transport_route_id = route.id
          this.manifest.disposal_method_id = route.disposal_method_id
          this.manifest.includeNonSubscribeTrader = route.include_non_subscribe_trader
          this.manifest.include_non_subscribe_trader = route.include_non_subscribe_trader
          this.refreshRecycleDisposerDisposalMethods(route)
        } else {
          if (func) {
            func();
          }
        }
      })

      const allRoutesPromise = this.fetchTransportRoutes({
        without_invisible: false,
        first_trader_owned: isCollection,
        self_emission: isSelfEmission,
        self_emission_include_own: isSelfEmission,
        proxy_request: isProxyRequest,
        general: isGeneral,
        item_id: this.manifest.item_id,
        item_type: this.callableType === 'general' ? 'GeneralItem' : 'Item',
        sales_item_price_code: this.manifest.sales_item_price_code
      }).then((content) => {
        this.allTransportRoutes = content.transport_routes
      })

      return Promise.all([visibleRoutesPromise, allRoutesPromise])
    },
    itemDropdownChangeEvent () {
      this.itemChangeEvent(this.manifest.item_id)
    },
    /**
     * 取扱品目の変更に応じて関連項目とルート候補を再計算する.
     * @param item_id
     */
    itemChangeEvent (item_id) {
      if (item_id) {
        const item = this.items.find( v => v.value === item_id)
        this.manifest.item_name = item?.text
        this.manifest.item_code = item?.code
        this.refreshTransportRoutes().then(() => {
          this.validateTransportRoute();
          this.refreshRecycleDisposerDisposalMethods();
        });
      } else {
        this.manifest.item_name = null;
        this.manifest.item_code = null;
        this.recycleDisposerDisposalMethods = []
      }
    },

    /**
     * 売上品目変更時に、品目コードと廃棄物名を引き継ぐ.
     * @param value
     */
    salesItemPriceChangeEvent (value) {
      const salesItemPriceDetail = this.salesItemPriceDetails.find(v => v.value === value)
      if (salesItemPriceDetail) {
        this.manifest.item_id = salesItemPriceDetail.item_id
        this.manifest.waste_name = salesItemPriceDetail.waste_name
        this.itemChangeEvent(this.manifest.item_id);
      } else {
        this.recycleDisposerDisposalMethods = []
      }
    },

    /**
     * 初期表示後、現在選択中のルートが候補内で有効かを確認する.
     */
    afterLoadRouteValidation () {
      if (this.manifest.can_change_transport_route) {
        if (this.selectedRouteRow) {
          this.selectedRouteRow.select(false);
          this.selectedRouteRow = null;
        }

        //　現在選択中のルートが、新しいルート一覧に存在するかチェック
        const transportRoute = this.transportRoutes.find(
          v => v.id === this.manifest.transport_route_id
        );

        if (!transportRoute) {
          if (this.manifest.transport_route_id) {
            this.showError({message: '選択中のルートは対象品目外のため解除されました'});
          }
          this.manifest.transport_route_id = null;
          this.manifest.includeNonSubscribeTrader = false;
          this.manifest.include_non_subscribe_trader = false;
        }
        this.refreshRecycleDisposerDisposalMethods(transportRoute)
      } else {
        this.refreshRecycleDisposerDisposalMethods(this.getSelectedTransportRoute())
      }
    },

    /**
     * 現在の売上品目に対してルートと関連項目の整合性を確認する.
     */
    validateTransportRoute() {
      const salesItemPriceDetail = this.salesItemPriceDetails.find(
        v => v.value === this.manifest.sales_item_price_code
      );

      // 予約登録済の場合はルートの変更不可
      if (this.manifest.can_change_transport_route) {
        if (this.selectedRouteRow) {
          this.selectedRouteRow.select(false);
          this.selectedRouteRow = null;
        }

        if (salesItemPriceDetail && salesItemPriceDetail.transport_route_id) {
          this.manifest.transport_route_id = salesItemPriceDetail.transport_route_id;
        }

        //　現在選択中のルートが、新しいルート一覧に存在するかチェック
        const transportRoute = this.transportRoutes.find(
          v => v.id === this.manifest.transport_route_id
        );

        if (transportRoute) {
          // this.manifest.transport_route_id = salesItemPriceDetail.transport_route_id
          this.manifest.disposal_method_id = transportRoute.disposal_method_id;
          this.manifest.includeNonSubscribeTrader = transportRoute.include_non_subscribe_trader;
          this.manifest.include_non_subscribe_trader = transportRoute.include_non_subscribe_trader;
        } else {
          if (this.manifest.transport_route_id) {
            this.showError({message: '選択中のルートは対象品目外のため解除されました'});
          }
          this.manifest.transport_route_id = null;
          this.manifest.disposal_method_id = null;
          this.manifest.includeNonSubscribeTrader = false;
          this.manifest.include_non_subscribe_trader = false;
          this.recycleDisposerDisposalMethods = []
        }
      }

      if(salesItemPriceDetail) {
        // その他項目
        this.manifest.packing_id = salesItemPriceDetail.packing_id;
        this.manifest.hazardous_substance_1_id = salesItemPriceDetail.hazardous_substance_1_id;
        this.manifest.hazardous_substance_2_id = salesItemPriceDetail.hazardous_substance_2_id;
        this.manifest.hazardous_substance_3_id = salesItemPriceDetail.hazardous_substance_3_id;
        this.manifest.hazardous_substance_4_id = salesItemPriceDetail.hazardous_substance_4_id;
        this.manifest.hazardous_substance_5_id = salesItemPriceDetail.hazardous_substance_5_id;
        this.manifest.hazardous_substance_6_id = salesItemPriceDetail.hazardous_substance_6_id;
      }

      this.refreshRecycleDisposerDisposalMethods()
    }
  }
}
</script>
